"use strict";

// Copyright 2012-2020 The MathWorks, Inc.
define(['dojo/_base/declare', 'dojo/_base/lang', 'mw-log/Log', 'mw-remote/RemoteEndPointsManager'], function (declare, lang, Log, RemoteEndPointsManager) {
  /**
   * The ResponseManager is responsible for handling the response to server requests. The
   * RequestManager hands the ResponseManager a Transaction object which will be un-packed. Each
   * message in the response will be sent to the registered response handler function. Response
   * handlers are registered through the "registerResponseHandler" function.
   */
  return declare([], {
    constructor: function constructor(args) {
      args = args || {};
      this.responseHandlers = args.responseHandlers || {};
      this.faultManager = args.faultManager;
      this.transactions = args.transactions || [];
    },

    /**
     * Register a handler function which is called to handle any response messages of the
     * specified type.
     * @param type Type of message that the registered function handles.
     * @param handlerFunction Function object to call. This will be passed the request context
     * and the response message.
     * @param scope Optional scope to bind the function to.
     */
    registerResponseHandler: function registerResponseHandler(type, handlerFunction, scope) {
      if (scope) {
        handlerFunction = lang.hitch(scope, handlerFunction);
      }

      this.responseHandlers[type] = handlerFunction;
    },

    /**
     * Deregisters a message response handler.
     * @param type Type of message.
     */
    deregisterResponseHandler: function deregisterResponseHandler(type) {
      delete this.responseHandlers[type];
    },

    /**
     * Handle a transaction. This is used by the RequestManager to hand off the transaction to
     * the response manager.
     * @param transaction The transaction to handle
     */
    handleTransaction: function handleTransaction(transaction) {
      this.transactions.push(transaction); // add the transaction to the stack.

      transaction.deferred.addCallback(lang.hitch(this, this._processAll, transaction));
      transaction.deferred.addCallback(lang.hitch(this, this._processResult, transaction));
      transaction.deferred.addErrback(lang.hitch(this, this._processAll, transaction));
      transaction.deferred.addErrback(lang.hitch(this, this._processFault, transaction));
    },

    /**
     * Callback added for both success and fault.
     * @param transaction
     */
    _processAll: function _processAll(transaction) {
      var index; // remove the transaction from the array

      index = this.transactions.indexOf(transaction);

      if (index >= 0) {
        this.transactions.splice(index, 1); // remove the transaction
      }
    },

    /**
     * Callback function for transactions.
     * @param transaction Transaction to process.
     */
    _processResult: function _processResult(transaction) {
      transaction.getResponseMessageTypes().forEach(function (responseType) {
        var responseMessages = transaction.responseMessages[responseType];
        var unclassifiedResponses = [];

        try {
          if (Array.isArray(responseMessages)) {
            responseMessages.forEach(function (responseMessage) {
              var typeWithClassifier;
              var classifier = transaction.getMessageClassifier(responseMessage);

              if (classifier) {
                typeWithClassifier = [responseType, classifier].join(':');
                var that = this;
                var handled = false;
                transaction.getRequestMessageTypes().forEach(function (requestType) {
                  var requestMessages = transaction.requestMessages[requestType];

                  if (Array.isArray(requestMessages)) {
                    requestMessages.forEach(function (requestMessage) {
                      if (that.responseHandlers[typeWithClassifier] && requestMessage.uuid === responseMessage.uuid) {
                        that.responseHandlers[typeWithClassifier]([responseMessage], transaction.context);
                        handled = true;
                      }
                    });
                  } else {
                    if (that.responseHandlers[typeWithClassifier] && requestMessages.uuid === responseMessage.uuid) {
                      that.responseHandlers[typeWithClassifier]([responseMessage], transaction.context);
                      handled = true;
                    }
                  }
                });

                if (!handled) {
                  // if no classified handler was found, then it should be handled
                  // by the registered unclassified response handler.
                  unclassifiedResponses.push(responseMessage);
                }
              } else {
                unclassifiedResponses.push(responseMessage);
              }
            }, this); // Only call handlers if there are unclassified responses

            if (unclassifiedResponses.length > 0 && this.responseHandlers[responseType]) {
              this.responseHandlers[responseType](unclassifiedResponses, transaction.context);
            }

            if (this.responseHandlers['*']) {
              this.responseHandlers['*'](responseType, transaction.responseMessages[responseType], transaction.context);
            }
          }
        } catch (e) {
          var jsonIndentation = 2;
          Log.error('Remote: Fault occurred while processing response.' + '\nMessage: ' + e.message + '\nType: ' + responseType + '\nResponse: ' + JSON.stringify(responseMessages, null, jsonIndentation));
        }
      }, this);

      this._processUdcResponses(transaction);

      if (transaction.fault) {
        this.faultManager.handleFault(transaction);
      }
    },

    /**
     * For non message container based messages, response structure is different. Normal message
     * container based response messages have following structure.
     * {
               "messages": {
                 "RouterLoginResponse": [
                   {
                     "userInfo": null,
                     "messageFaults": [
                       {
                         "message": "SecurityService.InvalidLoginCredentials",
                         "faultCode": "SecurityService.InvalidLoginCredentials"
                       }
                     ],
                     "uuid": "F356A3C4"
                   }
                 ]
               }
             }
     where as non message container based messages have following structure
     {"success":true,"messageFaults":[{
        "description": UserNotAuthorized
        "faultCode": User_Not_Authorized
     }]}. They are not wrapped inside the response type. Hence we tie response handlers to
     request types.
     * @param transaction
     * @private
     */
    _processUdcResponses: function _processUdcResponses(transaction) {
      var isMessageContainerBased = RemoteEndPointsManager.isEndpointMessageContainerBased(transaction.endPointName);

      if (!isMessageContainerBased) {
        var requestType = Object.keys(transaction.requestMessages)[0];

        if (this.responseHandlers[requestType]) {
          this.responseHandlers[requestType](transaction.responseMessages);
        }
      }
    },

    /**
     * Errback function for transactions.
     * @param transaction Transaction to process.
     */
    _processFault: function _processFault(transaction) {
      this.faultManager.handleFault(transaction);
    }
  });
});
//# sourceMappingURL=ResponseManager.js.map
